import type { ReactNode } from 'react';
import { toast as sonner } from 'sonner';

import { Toaster } from '@/components/ui/sonner';

/**
 * COMPATIBILITY SHIM — legacy toast API over sonner.
 *
 * The awkward part is shape, not styling: useToast() is a React hook reading
 * context, while sonner's `toast` is a module singleton. Keeping the hook and
 * delegating from it means all 22 call sites — and App.tsx's <ToastProvider> —
 * stay untouched. There is no context left, so the hook can no longer throw
 * when used outside a provider; sonner simply queues until <Toaster/> mounts.
 *
 * Legacy durations are preserved deliberately (3.5s, errors 5s) rather than
 * taking sonner's 4s default, so message timing does not shift under users.
 */

export type ToastTone = 'success' | 'error' | 'info';

const DEFAULT_MS = 3500;
const ERROR_MS = 5000;

interface ToastAPI {
  success: (message: string, durationMs?: number) => void;
  error: (message: string, durationMs?: number) => void;
  info: (message: string, durationMs?: number) => void;
  dismiss: (id: number) => void;
}

const api: ToastAPI = {
  success: (message, durationMs = DEFAULT_MS) => { sonner.success(message, { duration: durationMs }); },
  error: (message, durationMs = ERROR_MS) => { sonner.error(message, { duration: durationMs }); },
  info: (message, durationMs = DEFAULT_MS) => { sonner.info(message, { duration: durationMs }); },
  dismiss: (id) => { sonner.dismiss(id); },
};

export function useToast(): ToastAPI {
  return api;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      {/* bottom-center with a 96px offset matches the legacy viewport, which sat
          above the mobile bottom nav. richColors maps success/error/info onto
          sonner's semantic palette so tones survive dark mode. */}
      <Toaster position="bottom-center" offset={96} richColors closeButton />
    </>
  );
}
